// ============================================================================
// Checklist del expediente documental de un envío o reserva (CC-CONSOLE #1632).
// ============================================================================
// Lo usan la app «Documentación» (detalle del expediente), la pestaña
// «Documentos» del detalle de envío y la del detalle de reserva. Por tipo de
// documento: estado, quién lo entrega, versión vigente y vencimiento; subir o
// arrastrar con progreso, generar (#1631), aprobar/rechazar con nota, historial
// de versiones, línea de tiempo, descarga (URL firmada), visible al cliente y
// datos leídos por IA con «Aplicar». Sólo tokens semánticos: claro y oscuro.
// ============================================================================
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge, Button, EmptyState, Spinner, Switch, cn, type Tone } from "../../components/ui";
import { DOCUMENT_PROVIDERS, documentTypeName, isNeverClientVisible, type DocumentProvider, type DocumentStatus } from "../../utils/documentTypes";
import {
  documentsErrorMessage,
  generateMissingFields,
  isClientVisibleNotAllowedError,
  isExtractStaleError,
  isNotAvailable,
  isTooManyRequestsError,
  isVersionsLimitError,
  type ApplicableExtracted,
  type DocumentChecklist as Checklist,
  type DocumentGenerator,
  type DocumentSubjectRef,
  type ExtractionReason,
  type ShipmentDocument,
} from "../../services/shipmentDocuments";
import { openDownload, useDocumentActions, useDocumentChecklist, useDocumentGenerators, useDocumentTypes } from "./hooks";
import { CATEGORY_ICONS, DOCS_NS, MODE_ICONS, PROVIDER_ICONS, STATUS_ICONS, STATUS_TONES, completenessTone, expiryTone, formatBytes, groupByCategory, shortDate, shortDateTime, toDateInput } from "./meta";
import { UploadDropzone } from "./UploadDropzone";
import { ExtractedPanel } from "./ExtractedPanel";

// ---------------------------------------------------------------------------
// Piezas pequeñas
// ---------------------------------------------------------------------------

const TONE_TEXT: Record<Tone, string> = { neutral: "text-content-secondary", primary: "text-primary-500", success: "text-success", warning: "text-warning", error: "text-error", info: "text-info" };
const TONE_BG: Record<Tone, string> = { neutral: "bg-content-muted", primary: "bg-primary-500", success: "bg-success", warning: "bg-warning", error: "bg-error", info: "bg-info" };

export function StatusChip({ status, size = "sm" }: { status: DocumentStatus; size?: "sm" | "md" }) {
  const { t } = useTranslation(DOCS_NS);
  const label = t(`status.${status}`);
  return (
    <span role="status" aria-label={`${t("checklist.statusLabel")}: ${label}`} className="inline-flex">
      <Badge size={size} tone={STATUS_TONES[status]} dot>
        {label}
      </Badge>
    </span>
  );
}

export function ExpiryChip({ days, expiresAt }: { days: number | null; expiresAt: string | null }) {
  const { t, i18n } = useTranslation(DOCS_NS);
  if (days === null || !expiresAt) return null;
  const tone: Tone = expiryTone(days);
  const text = days <= 0 ? t("checklist.expiredAgo", { n: Math.abs(days) }) : t("checklist.expiresIn", { n: days });
  return (
    <span title={shortDate(expiresAt, i18n.language)} aria-label={text} className="inline-flex">
      <Badge size="sm" tone={tone}>
        <i className="ri-calendar-event-line" /> {text}
      </Badge>
    </span>
  );
}

function CompletenessBar({ percent, className }: { percent: number; className?: string }) {
  const v = Math.max(0, Math.min(100, Math.round(percent)));
  const tone = completenessTone(v);
  return (
    <div className={cn("h-2 w-full overflow-hidden rounded-full bg-subtle", className)} role="progressbar" aria-valuenow={v} aria-valuemin={0} aria-valuemax={100}>
      <div className={cn("h-full rounded-full transition-all", TONE_BG[tone])} style={{ width: `${v}%` }} />
    </div>
  );
}

function IconButton({ icon, label, onClick, disabled, tone = "neutral", loading }: { icon: string; label: string; onClick: () => void; disabled?: boolean; tone?: "neutral" | "success" | "error" | "primary"; loading?: boolean }) {
  const cls = { neutral: "text-content-secondary hover:bg-subtle hover:text-content", success: "text-success hover:bg-success/10", error: "text-error hover:bg-error/10", primary: "text-primary-500 hover:bg-primary-500/10" }[tone];
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled || loading}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn("flex h-7 w-7 items-center justify-center rounded-lg text-base disabled:cursor-not-allowed disabled:opacity-40", cls)}
    >
      {loading ? <Spinner size="sm" /> : <i className={icon} />}
    </button>
  );
}

const inputCls = "w-full rounded-lg border border-line bg-app px-2.5 py-1.5 text-sm text-content outline-none placeholder:text-content-faint focus:border-primary-500 disabled:opacity-60";

// ---------------------------------------------------------------------------
// Menú «Generar»
// ---------------------------------------------------------------------------

function GenerateMenu({ generators, loading, busy, onGenerate, compact }: { generators: DocumentGenerator[] | null | undefined; loading: boolean; busy: boolean; onGenerate: (g: DocumentGenerator) => void; compact?: boolean }) {
  const { t, i18n } = useTranslation(DOCS_NS);
  const [open, setOpen] = useState(false);
  const en = String(i18n.language || "es").startsWith("en");
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  const comingSoon = generators === null;
  const menuRef = useRef<HTMLDivElement>(null);
  // Flechas arriba/abajo recorren las opciones; Home/End saltan a los extremos.
  useEffect(() => {
    if (!open) return;
    const first = menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not([disabled])');
    first?.focus();
  }, [open, generators]);
  const onMenuKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? []);
    if (!items.length) return;
    e.preventDefault();
    const i = items.indexOf(document.activeElement as HTMLElement);
    const next = e.key === "Home" ? 0 : e.key === "End" ? items.length - 1 : e.key === "ArrowDown" ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
    items[next].focus();
  };
  return (
    <div className="relative">
      <Button size="sm" variant="secondary" icon={<i className="ri-magic-line" />} loading={busy} onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open}>
        {compact ? null : t("common.generate")}
      </Button>
      {open ? (
        <>
          <button type="button" aria-label={t("common.close")} className="fixed inset-0 z-40 cursor-default" onClick={() => setOpen(false)} />
          <div ref={menuRef} role="menu" aria-label={t("checklist.generate.title")} onKeyDown={onMenuKey} className="absolute right-0 z-50 mt-1 w-72 overflow-hidden rounded-xl border border-line bg-surface shadow-xl">
            <div className="border-b border-line px-3 py-2 text-xs font-semibold uppercase tracking-wide text-content-secondary">{t("checklist.generate.title")}</div>
            {loading ? (
              <div className="flex items-center gap-2 px-3 py-3 text-xs text-content-muted">
                <Spinner size="sm" /> {t("common.loading")}
              </div>
            ) : comingSoon ? (
              <div className="px-3 py-3 text-xs text-content-muted">
                <div className="mb-1 flex items-center gap-1 font-medium text-content">
                  <i className="ri-time-line" /> {t("common.comingSoon")}
                </div>
                {t("checklist.generate.comingSoonText")}
              </div>
            ) : !generators || generators.length === 0 ? (
              <div className="px-3 py-3 text-xs text-content-muted">{t("checklist.generate.none")}</div>
            ) : (
              <ul className="max-h-72 overflow-y-auto py-1">
                {generators.map((g) => {
                  const why = !g.available
                    ? g.reason === "mode"
                      ? t("checklist.generate.unavailableMode")
                      : t("checklist.generate.unavailableMissing", { fields: g.missing.map((m) => t(`checklist.generate.fieldName.${m}`, { defaultValue: m })).join(", ") })
                    : null;
                  return (
                    <li key={g.id}>
                      <button
                        type="button"
                        role="menuitem"
                        disabled={!g.available}
                        title={why ?? undefined}
                        onClick={() => {
                          setOpen(false);
                          onGenerate(g);
                        }}
                        className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm text-content hover:bg-subtle disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <i className={cn(g.existing ? "ri-file-check-line" : "ri-file-add-line", "mt-0.5 text-content-muted")} />
                        <span className="min-w-0">
                          <span className="block truncate">{en ? g.name.en : g.name.es}</span>
                          {why ? <span className="block text-[11px] text-warning">{why}</span> : g.existing ? <span className="block truncate text-[11px] text-content-muted">{t("checklist.generate.existing", { v: g.existing.current_version })}</span> : g.description ? <span className="block truncate text-[11px] text-content-muted">{en ? g.description.en : g.description.es}</span> : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fila del checklist
// ---------------------------------------------------------------------------

function DocumentRow({ doc, onOpen, onUpload, onDownload, onApprove, onReject, busy }: { doc: ShipmentDocument; onOpen: () => void; onUpload: () => void; onDownload: () => void; onApprove: () => void; onReject: () => void; busy: boolean }) {
  const { t, i18n } = useTranslation(DOCS_NS);
  const name = documentTypeName(doc.doc_type, i18n.language, doc.name);
  const reviewable = doc.effective_status === "recibido" || doc.effective_status === "en_revision";
  // Un <li> con DOS zonas: el botón que abre el panel (toda la descripción) y
  // una barra de acciones aparte; así no hay botones anidados dentro de un
  // "button" y cada control se alcanza con Tab.
  return (
    <li className="group flex items-center gap-2 px-3 py-2 hover:bg-subtle focus-within:bg-subtle" data-doc-id={doc.id}>
      <button
        type="button"
        onClick={onOpen}
        data-doc-open={doc.id}
        aria-label={`${name} · ${t(`status.${doc.effective_status}`)}`}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-subtle", TONE_TEXT[STATUS_TONES[doc.effective_status]])} aria-hidden="true">
          <i className={STATUS_ICONS[doc.effective_status]} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="truncate text-sm font-medium text-content">{name}</span>
            {!doc.required ? <span className="text-[10px] uppercase tracking-wide text-content-faint">{t("common.optional")}</span> : null}
            {doc.client_visible ? <i className="ri-eye-line text-xs text-content-faint" title={t("checklist.visibleToClient")} aria-label={t("checklist.visibleToClient")} /> : null}
          </span>
          <span className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-content-muted">
            <span className="inline-flex items-center gap-1">
              <i className={PROVIDER_ICONS[doc.provided_by]} /> {t(`provider.${doc.provided_by}`)}
            </span>
            {doc.has_file ? (
              <span className="inline-flex items-center gap-1">
                <i className="ri-file-3-line" /> v{doc.current_version}
              </span>
            ) : doc.reference ? (
              <span className="inline-flex items-center gap-1 font-mono">
                <i className="ri-hashtag" /> {doc.reference}
              </span>
            ) : (
              <span>{doc.reference_only ? t("checklist.referenceOnly") : t("checklist.noFile")}</span>
            )}
            {doc.updated_at ? <span>{shortDateTime(doc.updated_at, i18n.language)}</span> : null}
          </span>
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1">
          <StatusChip status={doc.effective_status} />
          <ExpiryChip days={doc.expires_in_days} expiresAt={doc.expires_at} />
        </span>
      </button>
      <div className="hidden shrink-0 items-center gap-0.5 sm:flex" role="group" aria-label={`${t("common.actions")}: ${name}`}>
        <IconButton icon={doc.reference_only ? "ri-hashtag" : "ri-upload-2-line"} label={doc.reference_only ? t("checklist.addReference") : doc.has_file ? t("checklist.uploadVersion") : t("checklist.uploadNew")} onClick={onUpload} disabled={busy} tone="primary" />
        <IconButton icon="ri-download-2-line" label={t("common.download")} onClick={onDownload} disabled={!doc.has_file || busy} />
        {reviewable ? (
          <>
            <IconButton icon="ri-check-line" label={t("checklist.approve")} onClick={onApprove} disabled={busy} tone="success" />
            <IconButton icon="ri-close-line" label={t("checklist.reject")} onClick={onReject} disabled={busy} tone="error" />
          </>
        ) : null}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Detalle de un documento (panel lateral)
// ---------------------------------------------------------------------------

type DrawerIntent = "view" | "upload" | "reject";

function DocumentDrawer({ doc, subject, intent, onClose, generators }: { doc: ShipmentDocument; subject: DocumentSubjectRef; intent: DrawerIntent; onClose: () => void; generators: DocumentGenerator[] | null | undefined }) {
  const { t, i18n } = useTranslation(DOCS_NS);
  const actions = useDocumentActions(subject);
  const name = documentTypeName(doc.doc_type, i18n.language, doc.name);

  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [reference, setReference] = useState(doc.reference ?? "");
  const [issuedAt, setIssuedAt] = useState(toDateInput(doc.issued_at));
  const [expiresAt, setExpiresAt] = useState(toDateInput(doc.expires_at));
  const [note, setNote] = useState("");
  const [rejectOpen, setRejectOpen] = useState(intent === "reject");
  const [rejectNote, setRejectNote] = useState("");
  const [downloading, setDownloading] = useState<number | null>(null);
  // La lectura con IA se da por disponible hasta que su ruta responda 404 (no se infiere de los generadores).
  const [extractAvailable, setExtractAvailable] = useState<boolean | null>(true);
  const [extractPending, setExtractPending] = useState(false);
  const [extractNotice, setExtractNotice] = useState<ExtractionReason | null>(null);
  const [tab, setTab] = useState<"detalle" | "versiones" | "historial">("detalle");
  const types = useDocumentTypes();
  // Carátula y borrador de Carta Porte nunca se muestran al cliente (espejo + catálogo del API).
  const neverVisible = isNeverClientVisible(doc.doc_type) || (types.data?.never_client_visible ?? []).includes(doc.doc_type);

  useEffect(() => {
    setReference(doc.reference ?? "");
    setIssuedAt(toDateInput(doc.issued_at));
    setExpiresAt(toDateInput(doc.expires_at));
  }, [doc.id, doc.reference, doc.issued_at, doc.expires_at]);

  // Accesibilidad del panel: foco al abrir, trampa de foco (Tab circula dentro),
  // Escape cierra y el foco vuelve a la fila que lo abrió.
  const asideRef = useRef<HTMLElement>(null);
  const openerRef = useRef<HTMLElement | null>(typeof document !== "undefined" ? (document.activeElement as HTMLElement | null) : null);
  useEffect(() => {
    const opener = openerRef.current;
    asideRef.current?.focus();
    return () => {
      const back = opener && opener.isConnected ? opener : (typeof document !== "undefined" ? (document.querySelector<HTMLElement>(`[data-doc-open="${CSS.escape(doc.id)}"]`) ?? null) : null);
      back?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab" || !asideRef.current) return;
      const focusables = Array.from(asideRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || active === asideRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const busy = actions.upload.isPending || actions.update.isPending || actions.generate.isPending || actions.confirmExtracted.isPending;
  const fail = (err: unknown, key: string) => toast.error(documentsErrorMessage(err, t(key)));

  const submitUpload = async () => {
    if (!file && !(doc.reference_only && reference.trim())) {
      toast.error(t(doc.reference_only ? "checklist.upload.referenceRequired" : "checklist.upload.fileRequired"));
      return;
    }
    setProgress(0);
    try {
      const result = await actions.upload.mutateAsync({
        input: { doc_type: doc.doc_type, file, reference: reference.trim() || null, issued_at: issuedAt ? new Date(issuedAt).toISOString() : null, expires_at: expiresAt ? new Date(expiresAt).toISOString() : null, note: note.trim() || null },
        onProgress: setProgress,
      });
      setFile(null);
      setNote("");
      setProgress(null);
      if (result.duplicate) {
        // Mismo contenido que la versión vigente: el API no creó otra versión.
        toast.info(t("checklist.upload.duplicate"));
        return;
      }
      toast.success(t("checklist.upload.done"));
      if (result.extraction?.reason === "queued") {
        // La IA lee en segundo plano (≤ 60 s): relee el documento hasta que llegue `extracted`.
        setExtractNotice(null);
        setExtractPending(true);
        void actions
          .pollExtraction(result.document.id)
          .catch(() => undefined)
          .finally(() => setExtractPending(false));
      } else if (result.extraction?.reason && result.extraction.reason !== "disabled" && result.extraction.reason !== "no_file") {
        setExtractNotice(result.extraction.reason);
      }
    } catch (err) {
      setProgress(null);
      if (isVersionsLimitError(err)) toast.error(t("checklist.upload.versionsLimit"));
      else fail(err, "checklist.upload.failed");
    }
  };

  const saveMeta = async (patch: Parameters<typeof actions.update.mutateAsync>[0]["patch"]) => {
    try {
      await actions.update.mutateAsync({ id: doc.id, patch });
      toast.success(t("checklist.saved"));
    } catch (err) {
      if (isClientVisibleNotAllowedError(err)) toast.warning(t("checklist.neverClientVisible"));
      else fail(err, "checklist.saveFailed");
    }
  };

  const setStatus = async (status: "aprobado" | "rechazado" | "en_revision" | "recibido" | "pendiente", statusNote?: string) => {
    try {
      await actions.update.mutateAsync({ id: doc.id, patch: { status, note: statusNote ?? null } });
      toast.success(t(`checklist.toast.${status}`));
      setRejectOpen(false);
      setRejectNote("");
    } catch (err) {
      fail(err, "checklist.saveFailed");
    }
  };

  const download = async (version?: number) => {
    setDownloading(version ?? -1);
    try {
      await openDownload(doc.id, version);
    } catch (err) {
      fail(err, "checklist.download.failed");
    } finally {
      setDownloading(null);
    }
  };

  const generator = generators?.find((g) => g.doc_type === doc.doc_type || g.id === doc.doc_type) ?? null;
  const runGenerate = async () => {
    if (!generator) return;
    try {
      const result = await actions.generate.mutateAsync({ docType: generator.doc_type });
      toast.success(t("checklist.generate.done"));
      if (result.download?.url && typeof window !== "undefined") window.open(result.download.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      const missing = generateMissingFields(err);
      if (missing) toast.warning(t("checklist.generate.missingData", { fields: missing.map((m) => t(`checklist.generate.fieldName.${m}`, { defaultValue: m })).join(", ") }));
      else if (isNotAvailable(err)) toast.info(t("checklist.generate.comingSoonText"));
      else fail(err, "checklist.generate.failed");
    }
  };

  const refreshExtracted = async () => {
    try {
      await actions.refresh(doc.id);
    } catch (err) {
      fail(err, "common.loadError");
    }
  };

  const runExtract = async (force: boolean) => {
    setExtractNotice(null);
    try {
      const result = await actions.extract.mutateAsync({ id: doc.id, force });
      setExtractAvailable(true);
      // `already_extracted`, `budget_exceeded`, `ai_not_configured`, `sandbox`, `too_large`…: avisos, no errores.
      if (result.extraction && !result.extraction.ran && result.extraction.reason) setExtractNotice(result.extraction.reason);
    } catch (err) {
      if (isNotAvailable(err)) setExtractAvailable(false);
      else if (isExtractStaleError(err)) setExtractNotice("version_stale");
      else if (isTooManyRequestsError(err)) setExtractNotice("too_many_requests");
      else fail(err, "checklist.extracted.failedToast");
    }
  };
  const applyExtracted = async (apply: ApplicableExtracted[]) => {
    try {
      await actions.confirmExtracted.mutateAsync({ id: doc.id, apply });
      toast.success(apply.length ? t("checklist.extracted.applied", { n: apply.length }) : t("checklist.extracted.confirmedToast"));
    } catch (err) {
      if (isNotAvailable(err)) setExtractAvailable(false);
      else fail(err, "checklist.extracted.failedToast");
    }
  };

  const reviewable = doc.effective_status === "recibido" || doc.effective_status === "en_revision";
  const hasContent = doc.has_file || Boolean(doc.reference);
  const versions = [...doc.versions].sort((a, b) => b.n - a.n);
  const history = [...doc.history].sort((a, b) => (b.at || "").localeCompare(a.at || ""));

  const tabs: Array<{ id: typeof tab; label: string; icon: string; count?: number }> = [
    { id: "detalle", label: t("checklist.tabs.detail"), icon: "ri-file-list-3-line" },
    { id: "versiones", label: t("checklist.tabs.versions"), icon: "ri-history-line", count: versions.length },
    { id: "historial", label: t("checklist.tabs.timeline"), icon: "ri-time-line", count: history.length },
  ];

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" role="dialog" aria-modal="true" aria-labelledby="document-drawer-title">
      <button type="button" aria-label={t("common.close")} className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" onClick={onClose} />
      <aside ref={asideRef} tabIndex={-1} className="relative flex h-full w-full max-w-xl flex-col border-l border-line bg-surface shadow-2xl outline-none">
        <header className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 id="document-drawer-title" className="truncate text-base font-semibold text-content">
              {name}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <StatusChip status={doc.effective_status} />
              <ExpiryChip days={doc.expires_in_days} expiresAt={doc.expires_at} />
              <Badge size="sm" tone="neutral">
                <i className={PROVIDER_ICONS[doc.provided_by]} /> {t(`provider.${doc.provided_by}`)}
              </Badge>
              {doc.source ? (
                <Badge size="sm" tone={doc.source === "generated" ? "primary" : "neutral"}>
                  {t(`source.${doc.source}`)}
                </Badge>
              ) : null}
              <span className="font-mono text-[10px] text-content-faint">{doc.doc_type}</span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {doc.has_file ? <IconButton icon="ri-download-2-line" label={t("common.download")} onClick={() => void download()} loading={downloading === -1} /> : null}
            <button type="button" onClick={onClose} aria-label={t("common.close")} className="flex h-8 w-8 items-center justify-center rounded-lg text-content-secondary hover:bg-subtle hover:text-content">
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </header>

        <div className="flex gap-1 border-b border-line px-3" role="tablist" aria-label={name}>
          {tabs.map((tb, i) => (
            <button
              key={tb.id}
              id={`doc-tab-${tb.id}`}
              role="tab"
              type="button"
              aria-selected={tab === tb.id}
              aria-controls={`doc-tabpanel-${tb.id}`}
              tabIndex={tab === tb.id ? 0 : -1}
              onClick={() => setTab(tb.id)}
              onKeyDown={(e) => {
                if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
                e.preventDefault();
                const next = tabs[(i + (e.key === "ArrowRight" ? 1 : tabs.length - 1)) % tabs.length].id;
                setTab(next);
                document.getElementById(`doc-tab-${next}`)?.focus();
              }}
              className={cn("flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium outline-none focus-visible:ring-2 focus-visible:ring-primary-500", tab === tb.id ? "border-primary-500 text-primary-500" : "border-transparent text-content-secondary hover:text-content")}
            >
              <i className={tb.icon} /> {tb.label}
              {tb.count !== undefined ? <span className="rounded-full bg-subtle px-1.5 text-[10px] tabular-nums">{tb.count}</span> : null}
            </button>
          ))}
        </div>

        <div id={`doc-tabpanel-${tab}`} role="tabpanel" aria-labelledby={`doc-tab-${tab}`} className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {tab === "detalle" ? (
            <>
              {reviewable || rejectOpen ? (
                <section className="rounded-xl border border-warning/40 bg-warning/5 p-3">
                  <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-content">
                    <i className="ri-search-eye-line text-warning" /> {t("checklist.review.title")}
                  </h4>
                  {!rejectOpen ? (
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="primary" icon={<i className="ri-check-line" />} loading={actions.update.isPending} onClick={() => void setStatus("aprobado")}>
                        {t("checklist.approve")}
                      </Button>
                      <Button size="sm" variant="outline" icon={<i className="ri-close-line" />} onClick={() => setRejectOpen(true)}>
                        {t("checklist.reject")}
                      </Button>
                      {doc.effective_status === "recibido" ? (
                        <Button size="sm" variant="ghost" icon={<i className="ri-eye-line" />} onClick={() => void setStatus("en_revision")}>
                          {t("checklist.markReview")}
                        </Button>
                      ) : null}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <label className="block text-xs font-medium text-content-secondary" htmlFor="reject-note">
                        {t("checklist.rejectNote")}
                      </label>
                      <textarea id="reject-note" value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} rows={2} maxLength={500} placeholder={t("checklist.rejectNotePlaceholder")} className={inputCls} />
                      <div className="flex gap-2">
                        <Button size="sm" variant="danger" icon={<i className="ri-close-circle-line" />} disabled={!rejectNote.trim()} loading={actions.update.isPending} onClick={() => void setStatus("rechazado", rejectNote.trim())}>
                          {t("checklist.confirmReject")}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setRejectOpen(false)}>
                          {t("common.cancel")}
                        </Button>
                      </div>
                    </div>
                  )}
                </section>
              ) : null}

              {doc.review ? (
                <div className={cn("rounded-xl border p-3 text-xs", doc.review.status === "aprobado" ? "border-success/40 bg-success/5" : "border-error/40 bg-error/5")}>
                  <div className="font-medium text-content">
                    {t(`status.${doc.review.status}`)} · {shortDateTime(doc.review.at, i18n.language)}
                  </div>
                  {doc.review.note ? <div className="mt-0.5 text-content-secondary">{doc.review.note}</div> : null}
                </div>
              ) : null}

              <section className="rounded-xl border border-line bg-card p-3" data-testid="document-upload-section">
                <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-content">
                  <i className="ri-upload-2-line text-primary-500" /> {doc.reference_only ? t("checklist.addReference") : doc.has_file ? t("checklist.uploadVersion") : t("checklist.uploadNew")}
                </h4>
                {!doc.reference_only ? <UploadDropzone file={file} onFile={setFile} progress={progress} disabled={busy} compact /> : null}
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <label className="block sm:col-span-2">
                    <span className="mb-1 block text-xs font-medium text-content-secondary">{t("checklist.reference")}</span>
                    <input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={120} placeholder={t("checklist.referencePlaceholder")} className={cn(inputCls, "font-mono")} />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-content-secondary">{t("checklist.upload.issuedAt")}</span>
                    <input type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} className={inputCls} />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-content-secondary">{t("checklist.upload.expiresAt")}</span>
                    <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className={inputCls} />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="mb-1 block text-xs font-medium text-content-secondary">{t("checklist.upload.note")}</span>
                    <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} className={inputCls} />
                  </label>
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <Button size="sm" variant="ghost" icon={<i className="ri-save-line" />} disabled={busy} onClick={() => void saveMeta({ reference: reference.trim() || null, issued_at: issuedAt ? new Date(issuedAt).toISOString() : null, expires_at: expiresAt ? new Date(expiresAt).toISOString() : null })}>
                    {t("checklist.saveMeta")}
                  </Button>
                  <div className="flex gap-2">
                    {generator ? (
                      <Button size="sm" variant="secondary" icon={<i className="ri-magic-line" />} loading={actions.generate.isPending} disabled={busy && !actions.generate.isPending} onClick={() => void runGenerate()}>
                        {t("common.generate")}
                      </Button>
                    ) : null}
                    <Button size="sm" icon={<i className={doc.reference_only ? "ri-hashtag" : "ri-upload-cloud-2-line"} />} loading={actions.upload.isPending} disabled={busy && !actions.upload.isPending} onClick={() => void submitUpload()}>
                      {doc.reference_only ? t("checklist.upload.submitReference") : t("checklist.upload.submit")}
                    </Button>
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-line bg-card p-3">
                <h4 className="mb-2 text-sm font-semibold text-content">{t("checklist.settings")}</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-content">
                      {t("checklist.visibleToClient")}
                      {neverVisible ? <span className="block text-[11px] text-content-muted">{t("checklist.neverClientVisible")}</span> : null}
                    </span>
                    <Switch checked={neverVisible ? false : doc.client_visible} disabled={busy || neverVisible} onChange={(v) => void saveMeta({ client_visible: v })} aria-label={t("checklist.visibleToClient")} />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-content">{t("common.required")}</span>
                    <Switch checked={doc.required} disabled={busy} onChange={(v) => void saveMeta({ required: v })} aria-label={t("common.required")} />
                  </div>
                  <label className="flex items-center justify-between gap-3">
                    <span className="text-sm text-content">{t("checklist.providedBy")}</span>
                    <select value={doc.provided_by} disabled={busy} onChange={(e) => void saveMeta({ provided_by: e.target.value as DocumentProvider })} className="rounded-lg border border-line bg-app px-2 py-1 text-sm text-content outline-none focus:border-primary-500">
                      {DOCUMENT_PROVIDERS.map((p) => (
                        <option key={p} value={p}>
                          {t(`provider.${p}`)}
                        </option>
                      ))}
                    </select>
                  </label>
                  {hasContent && !reviewable && doc.effective_status !== "aprobado" && doc.effective_status !== "generado" ? (
                    <Button size="sm" variant="ghost" icon={<i className="ri-inbox-archive-line" />} onClick={() => void setStatus("recibido")} disabled={busy}>
                      {t("checklist.markReceived")}
                    </Button>
                  ) : null}
                </div>
              </section>

              <ExtractedPanel document={doc} available={extractAvailable} pending={extractPending} running={actions.extract.isPending} applying={actions.confirmExtracted.isPending} notice={extractNotice} onRun={(force) => void runExtract(force)} onRefresh={() => void refreshExtracted()} onApply={(apply) => void applyExtracted(apply)} />
            </>
          ) : null}

          {tab === "versiones" ? (
            versions.length === 0 ? (
              <EmptyState icon="ri-file-3-line" title={t("checklist.versions.empty")} description={doc.reference ? `${t("checklist.reference")}: ${doc.reference}` : undefined} />
            ) : (
              <ul className="divide-y divide-line rounded-xl border border-line bg-card">
                {versions.map((v) => (
                  <li key={v.n} className="flex items-center gap-3 px-3 py-2">
                    <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-semibold", v.n === doc.current_version ? "bg-primary-500/10 text-primary-500" : "bg-subtle text-content-secondary")}>v{v.n}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-content">{v.file_name || "—"}</div>
                      <div className="text-[11px] text-content-muted">
                        {formatBytes(v.size)} · {shortDateTime(v.uploaded_at, i18n.language)}
                        {v.source ? ` · ${t(`source.${v.source}`)}` : ""}
                      </div>
                    </div>
                    {v.n === doc.current_version ? (
                      <Badge size="sm" tone="primary">
                        {t("checklist.versions.current")}
                      </Badge>
                    ) : null}
                    <IconButton icon="ri-download-2-line" label={t("common.download")} onClick={() => void download(v.n)} loading={downloading === v.n} />
                  </li>
                ))}
              </ul>
            )
          ) : null}

          {tab === "historial" ? (
            history.length === 0 ? (
              <EmptyState icon="ri-time-line" title={t("checklist.timeline.empty")} />
            ) : (
              <ol className="relative ml-2 space-y-3 border-l border-line pl-4">
                {history.map((h, i) => (
                  <li key={`${h.at}-${i}`} className="relative">
                    <span className={cn("absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full ring-2 ring-surface", h.status && STATUS_TONES[h.status] ? TONE_BG[STATUS_TONES[h.status]] : "bg-content-muted")} />
                    <div className="text-sm text-content">
                      {t(`checklist.timeline.action.${h.action}`, { defaultValue: h.action })}
                      {h.version ? ` · v${h.version}` : ""}
                      {h.status ? <span className="ml-2 text-xs text-content-secondary">({t(`status.${h.status}`)})</span> : null}
                    </div>
                    <div className="text-[11px] text-content-muted">{shortDateTime(h.at, i18n.language)}</div>
                    {h.note ? <div className="mt-0.5 text-xs text-content-secondary">{h.note}</div> : null}
                  </li>
                ))}
              </ol>
            )
          ) : null}
        </div>
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Checklist
// ---------------------------------------------------------------------------

export interface DocumentChecklistProps {
  subject: DocumentSubjectRef;
  /** Compacto para las pestañas de envío y reserva. */
  compact?: boolean;
  /** Encabezado con % y chips (por omisión sí). */
  showHeader?: boolean;
  /** Título opcional sobre el encabezado (p. ej. la referencia del sujeto). */
  title?: ReactNode;
  className?: string;
  /** Se llama cuando el expediente cambia (para refrescar listas externas). */
  onChanged?: (checklist: Checklist) => void;
}

export function DocumentChecklist({ subject, compact = false, showHeader = true, title, className, onChanged }: DocumentChecklistProps) {
  const { t } = useTranslation(DOCS_NS);
  const query = useDocumentChecklist(subject);
  const actions = useDocumentActions(subject);
  const generators = useDocumentGenerators(subject, Boolean(query.data?.materialized));
  const [drawer, setDrawer] = useState<{ id: string; intent: DrawerIntent } | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const data = query.data;
  useEffect(() => {
    if (data && onChanged) onChanged(data);
  }, [data, onChanged]);

  const groups = useMemo(() => groupByCategory(data?.documents ?? []), [data]);
  const current = drawer ? data?.documents.find((d) => d.id === drawer.id) ?? null : null;

  const fail = (err: unknown, key: string) => toast.error(documentsErrorMessage(err, t(key)));

  const materialize = async () => {
    try {
      const res = await actions.materialize.mutateAsync();
      toast.success(res.created?.length ? t("checklist.materialized", { n: res.created.length }) : t("checklist.materializedNone"));
    } catch (err) {
      fail(err, "checklist.materializeFailed");
    }
  };

  const quickStatus = async (doc: ShipmentDocument, status: "aprobado") => {
    try {
      await actions.update.mutateAsync({ id: doc.id, patch: { status } });
      toast.success(t(`checklist.toast.${status}`));
    } catch (err) {
      fail(err, "checklist.saveFailed");
    }
  };

  const download = async (doc: ShipmentDocument) => {
    setDownloading(doc.id);
    try {
      await openDownload(doc.id);
    } catch (err) {
      fail(err, "checklist.download.failed");
    } finally {
      setDownloading(null);
    }
  };

  const generate = async (g: DocumentGenerator) => {
    try {
      const result = await actions.generate.mutateAsync({ docType: g.doc_type });
      toast.success(t("checklist.generate.done"));
      if (result.download?.url && typeof window !== "undefined") window.open(result.download.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      const missing = generateMissingFields(err);
      if (missing) toast.warning(t("checklist.generate.missingData", { fields: missing.map((m) => t(`checklist.generate.fieldName.${m}`, { defaultValue: m })).join(", ") }));
      else if (isNotAvailable(err)) toast.info(t("checklist.generate.comingSoonText"));
      else fail(err, "checklist.generate.failed");
    }
  };

  if (query.isLoading) {
    return (
      <div className={cn("flex min-h-40 items-center justify-center gap-3 text-sm text-content-secondary", className)}>
        <Spinner /> {t("common.loading")}
      </div>
    );
  }
  if (query.isError || !data) {
    return (
      <div className={className}>
        <EmptyState icon="ri-error-warning-line" title={t("common.loadError")} description={documentsErrorMessage(query.error, "")} action={<Button variant="secondary" icon={<i className="ri-refresh-line" />} onClick={() => void query.refetch()}>{t("common.retry")}</Button>} />
      </div>
    );
  }

  const busy = actions.materialize.isPending || actions.update.isPending || actions.generate.isPending;
  const s = data.subject;

  return (
    <div className={cn("flex flex-col gap-3", className)} data-testid="document-checklist" data-tour="document-checklist">
      {showHeader ? (
        <div className={cn("rounded-xl border border-line bg-card", compact ? "p-3" : "p-4")}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              {title ? <div className="mb-1 truncate text-sm font-semibold text-content">{title}</div> : null}
              <div className="flex flex-wrap items-center gap-1.5 text-xs text-content-secondary">
                <Badge size="sm" tone="neutral">
                  <i className={MODE_ICONS[s.mode] ?? "ri-route-line"} /> {t(`mode.${s.mode}`)}
                </Badge>
                <Badge size="sm" tone="neutral">
                  {t(`direction.${s.direction}`)}
                </Badge>
                {s.incoterm ? (
                  <Badge size="sm" tone="primary">
                    {s.incoterm}
                  </Badge>
                ) : null}
                {s.client_name ? <span className="truncate">· {s.client_name}</span> : null}
              </div>
              <div className="mt-2 flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <CompletenessBar percent={data.percent} />
                </div>
                <span className="text-lg font-bold tabular-nums text-content">{data.percent}%</span>
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px]">
                <span className="text-content-muted">{t("checklist.completeness", { done: data.done_required, total: data.total_required })}</span>
                {data.missing.length ? (
                  <Badge size="sm" tone="error">
                    {t("checklist.missingN", { n: data.missing.length })}
                  </Badge>
                ) : null}
                {data.pending_review.length ? (
                  <Badge size="sm" tone="warning">
                    {t("checklist.pendingReviewN", { n: data.pending_review.length })}
                  </Badge>
                ) : null}
                {data.rejected.length ? (
                  <Badge size="sm" tone="error">
                    {t("checklist.rejectedN", { n: data.rejected.length })}
                  </Badge>
                ) : null}
                {data.expiring.length ? (
                  <Badge size="sm" tone="warning">
                    {t("checklist.expiringN", { n: data.expiring.length })}
                  </Badge>
                ) : null}
                {data.expired.length ? (
                  <Badge size="sm" tone="error">
                    {t("checklist.expiredN", { n: data.expired.length })}
                  </Badge>
                ) : null}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <Button size="sm" variant="ghost" icon={<i className="ri-refresh-line" />} onClick={() => void query.refetch()} aria-label={t("common.refresh")} title={t("common.refresh")} iconOnly />
              {data.materialized ? (
                <Button size="sm" variant="ghost" icon={<i className="ri-list-check-3" />} loading={actions.materialize.isPending} onClick={() => void materialize()} title={t("checklist.materializeHint")}>
                  {compact ? null : t("checklist.materializeAgain")}
                </Button>
              ) : null}
              <GenerateMenu generators={generators.data} loading={generators.isLoading} busy={actions.generate.isPending} onGenerate={(g) => void generate(g)} compact={compact} />
            </div>
          </div>
        </div>
      ) : null}

      {!data.materialized ? (
        <EmptyState
          icon="ri-file-add-line"
          title={t("checklist.empty")}
          description={t("checklist.emptyText", { n: data.requirements.length })}
          action={
            <Button icon={<i className="ri-list-check-3" />} loading={actions.materialize.isPending} onClick={() => void materialize()}>
              {t("checklist.materialize")}
            </Button>
          }
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-line bg-card">
          {groups.map((g) => (
            <div key={g.category}>
              <div className="flex items-center gap-2 border-b border-line bg-subtle/60 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-content-secondary">
                <i className={CATEGORY_ICONS[g.category]} /> {t(`category.${g.category}`)}
                <span className="ml-auto tabular-nums text-content-faint">
                  {g.documents.filter((d) => d.is_done).length}/{g.documents.length}
                </span>
              </div>
              <ul className="divide-y divide-line">
                {g.documents.map((doc) => (
                  <DocumentRow
                    key={doc.id}
                    doc={doc}
                    busy={busy || downloading === doc.id}
                    onOpen={() => setDrawer({ id: doc.id, intent: "view" })}
                    onUpload={() => setDrawer({ id: doc.id, intent: "upload" })}
                    onDownload={() => void download(doc)}
                    onApprove={() => void quickStatus(doc, "aprobado")}
                    onReject={() => setDrawer({ id: doc.id, intent: "reject" })}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {data.materialized && data.requirements.some((r) => !data.documents.find((d) => d.doc_type === r.doc_type)) ? (
        <p className="text-[11px] text-content-muted">
          <i className="ri-information-line" /> {t("checklist.templateChanged")}
        </p>
      ) : null}

      {current ? <DocumentDrawer key={`${current.id}-${drawer?.intent}`} doc={current} subject={subject} intent={drawer?.intent ?? "view"} onClose={() => setDrawer(null)} generators={generators.data} /> : null}
    </div>
  );
}

export default DocumentChecklist;
